import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRankingStore} from "data/stores/ranking/ranking.store";
import type {IRanking} from "data/providers/api/ranking.api.provider";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IUserStore} from "data/stores/user/user.store";

export interface ILeaderboardController extends ViewController {
	readonly i18n: ILocalizationStore;

	get leaderboards(): IRanking[];
	get isAuthorized(): boolean;
	get isNextPage(): boolean;

	loadMore: () => void;
}

@injectable()
export class LeaderboardController implements ILeaderboardController {
	get leaderboards() {
		return this._rankingStore.list;
	}

	get isNextPage() {
		return this._rankingStore.isNextPage;
	}

	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.RankingStore) public _rankingStore: IRankingStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore,
		@inject(Bindings.PlayersStore) public _playersStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	loadMore = () => {
		void this._rankingStore.fetchMoreLeaderboards();
	};

	async init() {
		this._rankingStore.resetPageCount();

		await Promise.all([
			this._squadsStore.fetchSquads(),
			this._playersStore.fetchPlayers(),
			this._rankingStore.fetchLeaderboards(),
		]);
	}
}
